import React, { useState, useEffect } from "react";
import ChatbotCard from "../ChatbotCard/ChatbotCard";
import Omnisearch from "../../components/Omnisearch/Omnisearch";
import { RiCheckboxCircleFill } from "react-icons/ri";
import { FaTrash } from "react-icons/fa";
import toast from "react-hot-toast";
import { IoIosCloseCircleOutline } from "react-icons/io";

import {
  getUsersPermission,
  deleteUserPermission,
  setUserPermission,
} from "../../services/api/ManagerService";

import { fetchUsers } from "../../services/api/AdminService";

function KnowledgeBaseCards({ datastores, onDelete }) {
  const [currentDatastores, setCurrentDatastores] = useState([]);
  const [selectedDatastore, setSelectedDatastore] = useState(null);
  const [chatbotUsers, setChatbotUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredAllUsers, setFilteredAllUsers] = useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUsersToChatbot, setAddUsersToChatbot] = useState([]);
  const [inlineConfirmId, setInlineConfirmId] = useState(null);

  useEffect(() => {
    if (datastores) {
      setCurrentDatastores(datastores);
    }
  }, [datastores]);

  useEffect(() => {
    setFilteredAllUsers(allUsers);
  }, [allUsers]);

  useEffect(() => {
    if (chatbotUsers.length > 0) {
      handleViewAllUsers();
    }
  }, [chatbotUsers]);

  const handleChatbotDeleted = (item) => {
    const filtered = currentDatastores.filter(
      (datastore) => datastore.id !== item.id
    );
    setCurrentDatastores(filtered);
    setInlineConfirmId(null);
    if (onDelete) {
      onDelete(filtered);
    }
  };

  const handleViewUsers = async (item) => {
    setShowAddUserModal(true);
    setSelectedDatastore(item.chatbot_name);
    try {
      const res = await getUsersPermission(item.chatbot_name);
      if (res) setChatbotUsers(res);
    } catch (err) {
      console.error("Error fetching users permission:", err);
    }
  };

  const handleCloseUsersView = () => {
    setSelectedDatastore(null);
    setChatbotUsers([]);
  };

  const handleDeleteUserPermission = async (chatbot_name, sub) => {
    try {
      await deleteUserPermission(chatbot_name, sub);
      const filteredUsers = chatbotUsers.filter((u) => u.sub !== sub);
      setChatbotUsers(filteredUsers);
      toast.success("User deleted correctly.");
    } catch {
      toast.error("Error deleting user permission.");
    }
  };

  const handleViewAllUsers = async () => {
    try {
      const res = await fetchUsers();
      if (res) {
        const filteredUsers = res.filter(
          (u) => !chatbotUsers.some((u2) => u.sub === u2.sub)
        );
        setAllUsers(filteredUsers);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearch = (target) => {
    if (target !== "") {
      const filtered = allUsers.filter((item) =>
        item.fullname.toLowerCase().includes(target.toLowerCase())
      );
      setFilteredAllUsers(filtered);
    } else {
      setFilteredAllUsers(allUsers);
    }
  };

  const toggleAddUserToChatbot = (sub) => {
    const user = allUsers.find((u) => u.sub === sub);
    const isAdded = addUsersToChatbot.some((u) => u.sub === sub);
    if (isAdded) {
      setAddUsersToChatbot(addUsersToChatbot.filter((u) => u.sub !== sub));
    } else {
      setAddUsersToChatbot([...addUsersToChatbot, user]);
    }
  };

  const handleAddUsersToChatbot = async () => {
    const newPermissions = addUsersToChatbot.map((u) => ({
      sub: u.sub,
      email: u.email,
      access: true,
    }));

    try {
      await setUserPermission(selectedDatastore, { users: newPermissions });
      toast.success("Users added correctly.");
      setChatbotUsers([...chatbotUsers, ...addUsersToChatbot]);
      setShowAddUserModal(false);
      setAddUsersToChatbot([]);
    } catch {
      toast.error("Error adding users.");
    }
  };

  const handleCloseAddUserModal = () => {
    setShowAddUserModal(false);
    setAddUsersToChatbot([]);
  };

  return (
    <>
      {showAddUserModal && (
        <div className="fixed top-0 right-0 w-full md:w-1/2 h-screen bg-white/80 backdrop-blur-2xl shadow-xl z-50 overflow-y-auto rounded-l-3xl border-l border-gray-300 p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">
              Who can access this chatbot?
            </h2>
            <button
              onClick={handleCloseAddUserModal}
              className="flex items-center text-sm text-gray-500 hover:text-primary transition"
            >
              <IoIosCloseCircleOutline className="text-2xl mr-1" /> Close
            </button>
          </div>

          {chatbotUsers.length > 0 && (
            <div className="mb-8 max-h-180 overflow-auto border rounded-md bg-white/90 shadow-inner">
              <table className="w-full text-sm text-left text-gray-700">
                <thead className="text-xs uppercase bg-gray-100 sticky top-0">
                  <tr>
                    <th className="p-2">Name</th>
                    <th className="p-2">Builder</th>
                    <th className="p-2">Admin</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {chatbotUsers.map((u, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="p-2">{u.fullname}</td>
                      <td className="p-2">
                        {u.is_builder && (
                          <RiCheckboxCircleFill className="text-primary text-xl" />
                        )}
                      </td>
                      <td className="p-2">
                        {u.is_admin && (
                          <RiCheckboxCircleFill className="text-primary text-xl" />
                        )}
                      </td>
                      <td className="p-2">
                        {!u.is_admin && !u.is_builder && (
                          <FaTrash
                            className="cursor-pointer text-red-600 hover:text-red-800"
                            onClick={() =>
                              handleDeleteUserPermission(
                                selectedDatastore,
                                u.sub
                              )
                            }
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 className="font-bold mb-3">Add new users</h3>
          <div className="mb-4">
            <Omnisearch onSearch={handleSearch} />
          </div>

          <div className="max-h-72 overflow-auto mb-6">
            {filteredAllUsers.map((u, i) => (
              <div
                key={i}
                className="flex justify-between items-center mb-2 px-2 py-1 rounded hover:bg-primary/10 cursor-pointer"
                onClick={() => toggleAddUserToChatbot(u.sub)}
              >
                <span>{u.fullname}</span>
                <input
                  type="checkbox"
                  checked={addUsersToChatbot.some((user) => user.sub === u.sub)}
                  readOnly
                />
              </div>
            ))}
          </div>

          <div className="flex justify-center">
            <button
              className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/50 transition cursor-pointer"
              onClick={handleAddUsersToChatbot}
              disabled={addUsersToChatbot.length === 0}
            >
              Confirm
            </button>
          </div>
        </div>
      )}

      <div className="px-8 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentDatastores.map((item, index) => (
            <ChatbotCard
              key={index}
              item={item}
              onDelete={handleChatbotDeleted}
              onUserView={handleViewUsers}
              inlineConfirmId={inlineConfirmId}
              setInlineConfirmId={setInlineConfirmId}
            />
          ))}
        </div>
      </div>
    </>
  );
}

export default KnowledgeBaseCards;
